import { useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { Category } from "../../categories/types";
import { SparePart, SparePartPayload } from "../types";

interface SparePartFormDialogProps {
  open: boolean;
  loading?: boolean;
  sparePart?: SparePart | null;
  categories?: Category[];
  error?: string;
  onClose: () => void;
  onSubmit: (payload: SparePartPayload) => Promise<void>;
}

const defaultValues: SparePartPayload = {
  name: "",
  categoryId: "",
  description: "",
  remarks: "",
  imageUrl: "",
  minimumStock: 0,
  maximumStock: 0,
  reorderLevel: 10,
  warrantyMonths: 0,
  maxQuantityPerServiceIssue: undefined,
};

const emptyToUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toNumberOrUndefined = (value?: number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const SparePartFormDialog = ({
  open,
  loading = false,
  sparePart,
  categories = [],
  error = "",
  onClose,
  onSubmit,
}: SparePartFormDialogProps) => {
  const { control, reset, handleSubmit } = useForm<SparePartPayload>({
    defaultValues,
  });

  useEffect(() => {
    if (sparePart) {
      reset({
        name: sparePart.name,
        categoryId: sparePart.categoryId,
        description: sparePart.description || "",
        remarks: sparePart.remarks || "",
        imageUrl: sparePart.imageUrl || "",
        minimumStock: sparePart.minimumStock || 0,
        maximumStock: sparePart.maximumStock || 0,
        reorderLevel: sparePart.reorderLevel ?? 10,
        warrantyMonths: sparePart.warrantyMonths || 0,
      });
    } else {
      reset(defaultValues);
    }
  }, [sparePart, reset]);

  const submit = (values: SparePartPayload) =>
    onSubmit({
      name: values.name.trim(),
      categoryId: values.categoryId,
      description: emptyToUndefined(values.description),
      remarks: emptyToUndefined(values.remarks),
      imageUrl: emptyToUndefined(values.imageUrl),
      minimumStock: toNumberOrUndefined(values.minimumStock),
      maximumStock: toNumberOrUndefined(values.maximumStock),
      reorderLevel: toNumberOrUndefined(values.reorderLevel),
      warrantyMonths: toNumberOrUndefined(values.warrantyMonths),
      ...(!sparePart
        ? {
            maxQuantityPerServiceIssue: toNumberOrUndefined(
              values.maxQuantityPerServiceIssue
            ),
          }
        : {}),
    });

  const hasCategories = categories.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {sparePart ? "Edit Spare Part" : "Create Spare Part"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Part name is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Part Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="categoryId"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label="Category"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      (categories.length ? "" : "Create a category first.")
                    }
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="minimumStock"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" fullWidth label="Minimum Stock" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="maximumStock"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" fullWidth label="Maximum Stock" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="reorderLevel"
                control={control}
                rules={{ required: "Reorder level is required", validate: (value) => Number.isInteger(Number(value)) && Number(value) >= 0 || "Enter a whole number of zero or greater" }}
                render={({ field, fieldState }) => (
                  <TextField {...field} type="number" fullWidth required label="Reorder Level" inputProps={{ min: 0, step: 1 }} error={!!fieldState.error} helperText={fieldState.error?.message} />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="warrantyMonths"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" fullWidth label="Warranty Months" />
                )}
              />
            </Grid>

            {!sparePart && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="maxQuantityPerServiceIssue"
                  control={control}
                  rules={{ required: "Maximum quantity per service issue is required", validate: (value) => Number.isInteger(Number(value)) && Number(value) > 0 || "Enter a positive whole number" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      type="number"
                      fullWidth
                      required
                      label="Maximum Quantity Per Service Issue"
                      inputProps={{ min: 1, step: 1 }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        "Maximum units that can be issued for this part on one service ticket."
                      }
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Controller
                name="imageUrl"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Image URL" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth multiline minRows={3} label="Description" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth multiline minRows={2} label="Remarks" />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={loading || !hasCategories}
          onClick={handleSubmit(submit)}
        >
          {sparePart ? "Update Spare Part" : "Create Spare Part"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SparePartFormDialog;
